import React, { Component, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import withConfig from '../../hoc/withConfig';
import { debounce } from '../../utils/debounceUtils';
import ClickAwayListener from '../clickAwayListener/clickAwayListener';
import SearchAutocompleteService from '../../services/searchAutocompleteService';
import BaiduSearchAutocompleteService from '../../services/baiduSearchAutocompleteService';
import withRouter from '../../hoc/withRouter';
import { parseQueryStrings } from '../../utils/urlParamUtils';
import { getDefaultSearchDistanceRadius } from '../../utils/searchFilterUtils';
import { generateRoute } from '../../utils/routeUtils';
import { PubSubEvents, Routes, SessionStorageKeys } from '../../constants';
import { getClassNameOrientation } from '../../utils/tenantUtils';
import Session from '../../session';
import APPLICATION_CONSTANTS from '../../applicationConstants';
import Events from '../../events';
import {
  removeCountryFromSuggestion,
  acsSuggestionByChainInputMatcher,
} from '../../utils/searchUtils';
import SuggestionsSkeleton from './suggestionsSkeleton';

class SearchAutocomplete extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showSuggestions: false,
      isFetchingSuggestions: false,
      searchSuggestions: [],
      acsSuggestionByChainResult: null,
      isInputValue: false
    };
    this._onSafeInputChange = debounce((value) => {
      if (value !== '') this.setState(() => ({ isFetchingSuggestions: true }));
      this._getSearchSuggestions(value);
    }, process.env.LOCATOR_SEARCH_DEBOUNCE_TIMING);
    this._searchAutocompleteService = new SearchAutocompleteService();
    this._baiduSearchAutocompleteService = new BaiduSearchAutocompleteService();
  }

  /**
   * generates payload that will be used by ACS
   */
  _generateAzureCognitiveSearchPayload = (address) => {
    const RESULT_LIMIT = 3;
    const { config } = this.props;
    const { search, hash } = this.props.location;

    let latLng = {
      lat: 0,
      lng: 0,
    };

    const queryStringValues = parseQueryStrings(search, hash);

    if (!queryStringValues.lat && !queryStringValues.long) {
      const userLocation = Session.getItem(
        SessionStorageKeys.userCurrentLocation
      );

      if (userLocation) {
        latLng = {
          lat: parseFloat(userLocation.lat),
          lng: parseFloat(userLocation.long),
        };
      }
    } else if (queryStringValues.lat && queryStringValues.long) {
      latLng = {
        lat: parseFloat(queryStringValues.lat),
        lng: parseFloat(queryStringValues.long),
      };
    }

    let distanceRadius = getDefaultSearchDistanceRadius(config);

    const { Distance, currentPage, filteredSearch } = queryStringValues;

    if (Distance && filteredSearch) {
      distanceRadius = Distance;
    }

    const page = currentPage ? parseInt(currentPage) : 1;

    const acsPayload = {
      indexSearchTerms: address,
      limit: RESULT_LIMIT,
      skip: (page - 1) * RESULT_LIMIT,
      latitude: latLng.lat,
      longitude: latLng.lng,
      distance: distanceRadius,
      locatorKey: APPLICATION_CONSTANTS.GLOBAL.CURRENT_TENANT,
    };

    return acsPayload;
  };

  /**
   * generates payload for ACS to Search Suggestions by Chain
   */
  _generateAzureCognitiveSearchSuggestionByChainPayload = (brandName) => {
    const RESULT_LIMIT = 3;
    const { config } = this.props;
    const { search, hash } = this.props.location;

    let latLng = {
      lat: 0,
      lng: 0,
    };

    const queryStringValues = parseQueryStrings(search, hash);

    if (!queryStringValues.lat && !queryStringValues.long) {
      const userLocation = Session.getItem(
        SessionStorageKeys.userCurrentLocation
      );

      if (userLocation) {
        latLng = {
          lat: parseFloat(userLocation.lat),
          lng: parseFloat(userLocation.long),
        };
      }
    } else if (queryStringValues.lat && queryStringValues.long) {
      latLng = {
        lat: parseFloat(queryStringValues.lat),
        lng: parseFloat(queryStringValues.long),
      };
    }

    let distanceRadius = getDefaultSearchDistanceRadius(config);

    const { Distance, currentPage, filteredSearch } = queryStringValues;

    if (Distance && filteredSearch) {
      distanceRadius = Distance;
    }

    const page = currentPage ? parseInt(currentPage) : 1;

    const acsByChainPayload = {
      indexSearchChain: brandName,
      limit: RESULT_LIMIT,
      skip: (page - 1) * RESULT_LIMIT,
      latitude: latLng.lat,
      longitude: latLng.lng,
      distance: distanceRadius,
      locatorKey: APPLICATION_CONSTANTS.GLOBAL.CURRENT_TENANT,
    };

    return acsByChainPayload;
  };

  /**
   * generates payload for ACS to Search Suggestions by Address
   */
  _generateAzureCognitiveSearchSuggestionByAddressPayload = (address) => {
    const RESULT_LIMIT = 3;
    const { config } = this.props;
    const { search, hash } = this.props.location;

    let latLng = {
      lat: 0,
      lng: 0,
    };

    const queryStringValues = parseQueryStrings(search, hash);

    if (!queryStringValues.lat && !queryStringValues.long) {
      const userLocation = Session.getItem(
        SessionStorageKeys.userCurrentLocation
      );

      if (userLocation) {
        latLng = {
          lat: parseFloat(userLocation.lat),
          lng: parseFloat(userLocation.long),
        };
      }
    } else if (queryStringValues.lat && queryStringValues.long) {
      latLng = {
        lat: parseFloat(queryStringValues.lat),
        lng: parseFloat(queryStringValues.long),
      };
    }

    let distanceRadius = getDefaultSearchDistanceRadius(config);

    const { Distance, currentPage, filteredSearch } = queryStringValues;

    if (Distance && filteredSearch) {
      distanceRadius = Distance;
    }

    const page = currentPage ? parseInt(currentPage) : 1;

    const acsByAddressPayload = {
      IndexSearchTerms: address,
      limit: RESULT_LIMIT,
      skip: (page - 1) * RESULT_LIMIT,
      latitude: latLng.lat,
      longitude: latLng.lng,
      distance: distanceRadius,
      locatorKey: APPLICATION_CONSTANTS.GLOBAL.CURRENT_TENANT,
    };

    return acsByAddressPayload;
  };

  _removeCountryFromSuggestions = (suggestions) => {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      description: removeCountryFromSuggestion(suggestion.description),
    }));
  };

  // sampleResponseObj = {
  //   adcode: "510107",
  //   address: "四川省 成都市",
  //   business: "",
  //   children: [],
  //   city: "成都市",
  //   cityid: "75",
  //   district: "武侯区",
  //   location: { lat: '0.578994', lng: '04.072747' },
  //   name: "成都市",
  //   province: "四川省",
  //   tag: "",
  //   uid: "df3970fe28cd251103ec01e6"
  // };

  // We are using object name for now (to be confirmed)
  _getBaiduSearchSuggestions = async (searchInputValue) => {
    const results = await this._baiduSearchAutocompleteService.getSuggestions(
      searchInputValue
    );

    return results.map(({ uid, name }) => ({
      identifier: uid,
      description: name,
      source: 'baidu',
    }));
  };

  _getAcsSuggestions = async (searchInputValue) => {
    const payload = this._generateAzureCognitiveSearchPayload(searchInputValue);
    const results =
      await this._searchAutocompleteService.getAzureCognitiveSearchSuggestions(
        payload
      );

    return results.suggestions.map(({ ecpId, ecpName }) => ({
      identifier: ecpId,
      description: ecpName,
      source: 'acs',
    }));
  };

  _getAcsSuggestionsByChain = async (brandName) => {
    const payload =
      this._generateAzureCognitiveSearchSuggestionByChainPayload(brandName);
    const results =
      await this._searchAutocompleteService.getAzureCognitiveSearchSuggestionByChain(
        payload
      );

    return results.suggestions.map(({ displayName, distance, chainName }) => ({
      identifier: distance,
      description: displayName,
      chainName: chainName,
      source: 'acsByChain',
    }));
  };

  _getGoogleSuggestions = async (searchInputValue) => {
    const { config } = this.props;
    const googleSuggestions = await this._searchAutocompleteService.findPlaces(
      searchInputValue,
      config.country_geo_constraint
    );
    const shortenedGoogleSuggestions =
      this._removeCountryFromSuggestions(googleSuggestions);

    return shortenedGoogleSuggestions.map(({ place_id, description }) => ({
      identifier: place_id,
      description: description,
      source: 'google',
    }));
  };

  _getSearchSuggestionByAddress = async (searchInputValue) => {
    const payload = this._generateAzureCognitiveSearchSuggestionByAddressPayload(searchInputValue);
    const results = await this._searchAutocompleteService.getAzureCognitiveSearchSuggestionByAddress(payload);

    return results.suggestions.map(({ address, lat, lng }) => ({
      identifier: address,
      description: address,
      source: 'acsByAddress',
      location: {
        addressLat: lat,
        addressLng: lng
      },
    }));
  }

  _getSearchSuggestions = async (searchInputValue) => {
    if (searchInputValue === '') {
      return;
    }

    try {
      let placeSuggestions = [];
      let acsSuggestionByChainName = '';
      let isGooglePlaceSuggestionEmpty = false;
      let addressSuggestions = [];
      const { use_baidu_map, enableSearchSuggestionsByAddress, enable_no_search_location_result_suggestion_display } = this.props.config;
      const brandName = acsSuggestionByChainInputMatcher(searchInputValue);
      const acsSuggestions = await this._getAcsSuggestions(searchInputValue);

      if (brandName) {
        acsSuggestionByChainName = await this._getAcsSuggestionsByChain(
          brandName
        );
      }

      if (use_baidu_map) {
        placeSuggestions = await this._getBaiduSearchSuggestions(
          searchInputValue
        );
      } else {
        placeSuggestions = await this._getGoogleSuggestions(searchInputValue);
        isGooglePlaceSuggestionEmpty = placeSuggestions.length === 0;
      }

      if ( isGooglePlaceSuggestionEmpty && enableSearchSuggestionsByAddress ) {
        addressSuggestions = await this._getSearchSuggestionByAddress(searchInputValue);
      }

      const combinedSuggestions = [
        ...acsSuggestionByChainName,
        ...acsSuggestions,
        ...placeSuggestions.slice(0, 3),
        ...addressSuggestions
      ];
      const setShowNoResultFound = enable_no_search_location_result_suggestion_display ?
        this.state.isInputValue : combinedSuggestions?.length > 0;

      this.setState(() => ({
        showSuggestions: setShowNoResultFound, 
        isFetchingSuggestions: false,
        searchSuggestions: combinedSuggestions || [],
        acsSuggestionByChainResult: acsSuggestionByChainName,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  _getSearchSuggestionItems = () => {
    const { searchSuggestions } = this.state;
    const { config, t } = this.props;
    const { enable_rtl_orientation } = config;
    return searchSuggestions.map((suggestion) => (
      <li key={suggestion.identifier}>
        <button
          className={getClassNameOrientation(
            'search-autocomplete__suggestion',
            enable_rtl_orientation
          )}
          onClick={() => this.onClickSuggestion(suggestion)}
        >
          {suggestion.description}{' '}
          {suggestion.source === 'acsByChain' && t('text_locations')}
        </button>
      </li>
    ));
  };

  onClickSuggestion = ({ identifier, description, source , location}) => {
    const { onSelectSuggestion } = this.props;

    if (source === 'acs') {
      this._onAcsSuggestionClick(identifier);
      return;
    } else if (source === 'acsByChain') {
      const chainName = this.state.acsSuggestionByChainResult[0].chainName;

      Events.emit(PubSubEvents.appendChainSearch, chainName);
      onSelectSuggestion(description, source);
      this.onClickAway();
    } else if (source === 'acsByAddress') {
      onSelectSuggestion(description, source, location);
      this.onClickAway();
    } else {
      const chainName = null;

      Events.emit(PubSubEvents.appendChainSearch, chainName);
      Events.emit(PubSubEvents.showUserLocation, false);
      onSelectSuggestion(description, source);
      this.onClickAway();
    }
  };

  _onAcsSuggestionClick = (practiceId) => {
    const { config, navigate, location } = this.props;
    const homeRoute = generateRoute(config, Routes.home);
    const detailsRoute = generateRoute(config, Routes.details);
    const resultsRoute = generateRoute(config, Routes.results);
    const { search } = location;

    navigate(`/${detailsRoute}/${practiceId}`, {
      state: {
        prevPage: search !== '' ? `${resultsRoute}${search}` : homeRoute,
      },
    });
  };

  _onChange = (value) => {
    const { onChange } = this.props;
    onChange(value);

    this._onSafeInputChange(value);

    if (value) {
      this.setState({ isInputValue: true });
    }
  };

  onClickAway = () => {
    this.setState(() => ({ showSuggestions: false }));
  };

  _onGetUserLocation = () => {
    const { onGetUserLocation } = this.props;
    Events.emit(PubSubEvents.showUserLocation, true);
    onGetUserLocation();
    this.onClickAway();
  };

  _onKeyDown = (e) => {
    const { searchSuggestions } = this.state;

    if (e.key === 'Enter' && searchSuggestions.length > 0) {
      // on Enter, automatically select the first suggestion
      this.onClickSuggestion(searchSuggestions[0]);
    }
  };

  render() {
    const { showSuggestions, isFetchingSuggestions, searchSuggestions } = this.state;
    const { locatorRootUri } = window.locatorTenancy;
    const { t, value, config, placeholder, onClearSearch } = this.props;
    const { enable_rtl_orientation, enable_no_search_location_result_suggestion_display } = config;
    const showNoResultsSuggestion = showSuggestions &&
      searchSuggestions.length === 0 &&
      enable_no_search_location_result_suggestion_display;

    return (
      <ClickAwayListener onClickAway={this.onClickAway}>
        <div className="search-autocomplete">
          {config.show_search_icon && (
            <div
              className={getClassNameOrientation(
                'search-autocomplete__search',
                enable_rtl_orientation
              )}
            >
              <img
                alt="Search icon"
                className={getClassNameOrientation(
                  'search-autocomplete__icon',
                  enable_rtl_orientation
                )}
                src={`${locatorRootUri}/assets/icons/essilor_search.png`}
              />
            </div>
          )}
          <input
            aria-autocomplete="list"
            aria-expanded={false}
            autoComplete="off"
            className={getClassNameOrientation(
              'search-autocomplete__input',
              enable_rtl_orientation
            )}
            placeholder={placeholder}
            type="text"
            onKeyDown={(e) => this._onKeyDown(e)}
            value={value}
            onChange={(e) => this._onChange(e.target.value)}
          />
          {config.show_search_clear_button && (
            <button
              className={getClassNameOrientation(
                'search-autocomplete__clear',
                enable_rtl_orientation
              )}
              onClick={onClearSearch}
            >
              <span className="search-autocomplete__clear-icon"></span>
            </button>
          )}

          {isFetchingSuggestions ? (
            <SuggestionsSkeleton />
          ) : (
            <ul
              className={`search-autocomplete__suggestions ${
                showSuggestions
                  ? 'search-autocomplete__suggestions--visible'
                  : ''
              }`}
            >
              {showNoResultsSuggestion ? (
                <li>
                  <button
                    className="search-autocomplete__no-result"
                    onClick={this._onGetUserLocation}
                  >
                    <div className="search-autocomplete__geo">
                      <img
                        alt="Geolocate"
                        className="search-autocomplete__icon-geo"
                        src={`${locatorRootUri}/assets/icons/essilor_geo_locate.png`}
                      />
                    </div>
                  </button>
                  <span>
                    {t('text_no_results')}
                  </span>
                </li>
              ):(
                <>
                  <li>
                    <button
                      className="search-autocomplete__suggestion"
                      onClick={this._onGetUserLocation}
                    >
                      <div className="search-autocomplete__geo">
                        <img
                          alt="Geolocate"
                          className="search-autocomplete__icon-geo"
                          src={`${locatorRootUri}/assets/icons/essilor_geo_locate.png`}
                        />
                      </div>
                      {t('text_use_location')}
                    </button>
                  </li>
                  <li>
                    <hr className="search-autocomplete__divider" />
                  </li>
                </>
              )}
              {this._getSearchSuggestionItems()}
            </ul>
          )}
        </div>
      </ClickAwayListener>
    );
  }
}

SearchAutocomplete.propTypes = {
  t: PropTypes.func.isRequired,
  navigate: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([undefined])  // Explicitly allows undefined
  ]).isRequired,
  config: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  location: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onGetUserLocation: PropTypes.func.isRequired,
  onSelectSuggestion: PropTypes.func.isRequired,
};

SearchAutocomplete.defaultProps = {
  placeholder: '',
};

export default withConfig(withRouter(withTranslation()(SearchAutocomplete)));
